import { useState, type ChangeEvent } from 'react';
import { Brush2, Cross, Setting2, Tag } from '@/components/icons';
import { Checkbox } from '@/components/ui/checkbox';
import CollapsibleSection from '@/components/ui/collapsible-section';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { DEFAULT_DISTRIBUTION_BAND_COLOR } from '@/lib/chart-config';
import BarStyleControls from './BarStyleControls';
import ColorSwatchPicker from './ColorSwatchPicker';
import DecorationsEditor from './DecorationsEditor';
import FieldLabel from './FieldLabel';
import LineStyleControls from './LineStyleControls';
import SeriesColorList from './SeriesColorList';
import type { BandRow, CutoffRow } from './types';
import type {
    AxisEndLabelsEditorValue,
    ChartTypeDefinition,
    QuadrantEditorValue,
} from '../chart-types/registry';

interface StyleStepProps {
    typeDef: ChartTypeDefinition;
    hasTable: boolean;
    title: string;
    onTitleChange: (value: string) => void;
    // Series colors + line style.
    columns: { id: string; label: string }[];
    selectedColumnIds: string[];
    seriesColors: Record<string, string>;
    primaryColor: string;
    secondaryColor: string;
    onSeriesColorChange: (columnId: string, color: string) => void;
    // Per-series marker shapes (line/scatter only).
    symbolsSupported: boolean;
    seriesSymbols: Record<string, string>;
    onSeriesSymbolChange: (columnId: string, symbol: string) => void;
    /** Distribution-only: single base color the classification tones derive from. */
    bandColor: string;
    onBandColorChange: (color: string) => void;
    referenceColumnIds: string[];
    lineCurve: 'straight' | 'smooth';
    fillArea: boolean;
    fillGradient: boolean;
    onLineCurveChange: (curve: 'straight' | 'smooth') => void;
    onFillAreaChange: (fill: boolean) => void;
    onFillGradientChange: (gradient: boolean) => void;
    // Bar appearance (bar only).
    barCorner: 'square' | 'soft' | 'round';
    barThickness: 'thin' | 'normal' | 'thick';
    barGradient: boolean;
    onBarCornerChange: (corner: 'square' | 'soft' | 'round') => void;
    onBarThicknessChange: (thickness: 'thin' | 'normal' | 'thick') => void;
    onBarGradientChange: (gradient: boolean) => void;
    // Per-point score labels (bar/line/scatter only).
    pointLabelsSupported: boolean;
    pointLabels: boolean;
    onPointLabelsChange: (show: boolean) => void;
    // Per-group bar color families (single-series bar with a group column only).
    colorByGroupSupported: boolean;
    colorByGroup: boolean;
    onColorByGroupChange: (enabled: boolean) => void;
    // Per-category emphasis (single-series bar/line/scatter only).
    pointEmphasisSupported: boolean;
    /** True when reference bands exist, enabling the classification tint. */
    classificationAvailable: boolean;
    colorByClassification: boolean;
    onColorByClassificationChange: (enabled: boolean) => void;
    /** Plottable categories (hidden rows excluded). */
    xLabels: string[];
    pointColors: Record<string, string>;
    /** Empty color = back to the series color (drops the override). */
    onPointColorChange: (label: string, color: string) => void;
    highlightLabel: string;
    highlightColor: string;
    onHighlightLabelChange: (label: string) => void;
    onHighlightColorChange: (color: string) => void;
    // Test–retest evolution labels (bar/line with exactly 2 data series).
    deltasSupported: boolean;
    showDeltas: boolean;
    onShowDeltasChange: (show: boolean) => void;
    // Decorations (cartesian bands/cutoffs/scale; reused as zones/range for gauge/bullet).
    showDecorations: boolean;
    bands: BandRow[];
    cutoffs: CutoffRow[];
    yAxisMin: string;
    yAxisMax: string;
    onAddBand: () => void;
    onUpdateBand: (index: number, patch: Partial<BandRow>) => void;
    onRemoveBand: (index: number) => void;
    onAddCutoff: () => void;
    onUpdateCutoff: (index: number, patch: Partial<CutoffRow>) => void;
    onRemoveCutoff: (index: number) => void;
    onYAxisMinChange: (value: string) => void;
    onYAxisMaxChange: (value: string) => void;
    // Per-type ConfigPanel (xy-scatter quadrants + axis-end labels).
    quadrant: QuadrantEditorValue;
    onQuadrantChange: (patch: Partial<QuadrantEditorValue>) => void;
    axisEndLabels: AxisEndLabelsEditorValue;
    onAxisEndLabelsChange: (patch: Partial<AxisEndLabelsEditorValue>) => void;
}

// Radix Select items cannot carry an empty value, so "none" rides sentinels.
const NO_HIGHLIGHT = '__none__';

// The collapsible style groups; one open at a time (like the Personalizar modal).
type StyleSection = 'appearance' | 'labels' | 'advanced';

/** Step 2 of the wizard: presentation (title, legend) + a collapsed advanced panel. */
export default function StyleStep({
    typeDef,
    hasTable,
    title,
    onTitleChange,
    showDecorations,
    bands,
    cutoffs,
    yAxisMin,
    yAxisMax,
    onAddBand,
    onUpdateBand,
    onRemoveBand,
    onAddCutoff,
    onUpdateCutoff,
    onRemoveCutoff,
    onYAxisMinChange,
    onYAxisMaxChange,
    quadrant,
    onQuadrantChange,
    axisEndLabels,
    onAxisEndLabelsChange,
    columns,
    selectedColumnIds,
    seriesColors,
    primaryColor,
    secondaryColor,
    onSeriesColorChange,
    symbolsSupported,
    seriesSymbols,
    onSeriesSymbolChange,
    bandColor,
    onBandColorChange,
    referenceColumnIds,
    lineCurve,
    fillArea,
    fillGradient,
    onLineCurveChange,
    onFillAreaChange,
    onFillGradientChange,
    barCorner,
    barThickness,
    barGradient,
    onBarCornerChange,
    onBarThicknessChange,
    onBarGradientChange,
    pointLabelsSupported,
    pointLabels,
    onPointLabelsChange,
    colorByGroupSupported,
    colorByGroup,
    onColorByGroupChange,
    pointEmphasisSupported,
    classificationAvailable,
    colorByClassification,
    onColorByClassificationChange,
    xLabels,
    pointColors,
    onPointColorChange,
    highlightLabel,
    highlightColor,
    onHighlightLabelChange,
    onHighlightColorChange,
    deltasSupported,
    showDeltas,
    onShowDeltasChange,
}: StyleStepProps) {
    const { ConfigPanel, decorationsCopy } = typeDef;
    // Unset per-category swatches preview the effective series color.
    const seriesBaseColor = seriesColors[selectedColumnIds[0]] || primaryColor;
    const hasAdvanced = showDecorations || !!ConfigPanel;
    // The user's brand colors, offered as the leading swatches in every picker.
    const brandColors = [primaryColor, secondaryColor];

    // Single-open sections, mirroring the Personalizar modal's accordion behavior.
    const [activeSection, setActiveSection] = useState<StyleSection | null>('appearance');
    const toggleSection = (section: StyleSection) =>
        setActiveSection((prev) => (prev === section ? null : section));

    // The advanced panel title adapts to what it actually contains for this type.
    const advancedTitle =
        decorationsCopy?.sectionTitle ??
        (showDecorations ? 'Faixas e limites' : ConfigPanel ? 'Quadrantes' : 'Ajustes avançados');

    // Which grouped accordions have any control for the current chart type — an
    // empty group is dropped rather than shown as a dead expander.
    const hasLabelsGroup =
        pointLabelsSupported || colorByGroupSupported || deltasSupported || pointEmphasisSupported;

    return (
        <div className="space-y-3">
            {/* APARÊNCIA — title, series colors, and the per-geometry look controls. */}
            <CollapsibleSection
                title="Aparência"
                icon={Brush2}
                open={activeSection === 'appearance'}
                onToggle={() => toggleSection('appearance')}
            >
                <div>
                    <FieldLabel optional htmlFor="chart-title">
                        Título do gráfico
                    </FieldLabel>
                    <Input
                        id="chart-title"
                        value={title}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onTitleChange(event.target.value)
                        }
                        placeholder="Ex.: Perfil de subtestes"
                        disabled={!hasTable}
                    />
                </div>

                <SeriesColorList
                    multiSeries={typeDef.supports.multiSeries}
                    columns={columns}
                    selectedColumnIds={selectedColumnIds}
                    seriesColors={seriesColors}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    referenceColumnIds={referenceColumnIds}
                    symbolsSupported={symbolsSupported}
                    seriesSymbols={seriesSymbols}
                    disabled={!hasTable}
                    onChange={onSeriesColorChange}
                    onSymbolChange={onSeriesSymbolChange}
                />

                {typeDef.id === 'distribution' && (
                    <div>
                        <FieldLabel>Cor do fundo</FieldLabel>
                        <ColorSwatchPicker
                            value={bandColor}
                            defaultColor={DEFAULT_DISTRIBUTION_BAND_COLOR}
                            brandColors={brandColors}
                            onChange={onBandColorChange}
                            disabled={!hasTable}
                            ariaLabel="Cor do fundo da curva"
                        />
                    </div>
                )}

                {typeDef.id === 'line' && (
                    <LineStyleControls
                        curve={lineCurve}
                        fill={fillArea}
                        fillGradient={fillGradient}
                        disabled={!hasTable}
                        onCurveChange={onLineCurveChange}
                        onFillChange={onFillAreaChange}
                        onFillGradientChange={onFillGradientChange}
                    />
                )}

                {typeDef.id === 'bar' && (
                    <BarStyleControls
                        corner={barCorner}
                        thickness={barThickness}
                        gradient={barGradient}
                        disabled={!hasTable}
                        onCornerChange={onBarCornerChange}
                        onThicknessChange={onBarThicknessChange}
                        onGradientChange={onBarGradientChange}
                    />
                )}

                {/* Per-category colors — a color control, so it lives with appearance. */}
                {pointEmphasisSupported && xLabels.length > 0 && (
                    <div>
                        <FieldLabel hint="Uma cor escolhida aqui vence a cor da série e a da classificação.">
                            Cores por categoria
                        </FieldLabel>
                        <ul className="space-y-1.5">
                            {xLabels.map((label) => (
                                <li
                                    key={label}
                                    className="flex items-center justify-between gap-2 text-sm text-foreground"
                                >
                                    <span className="min-w-0 flex-1 truncate">{label}</span>
                                    <div className="flex items-center gap-1.5">
                                        {pointColors[label] && (
                                            <button
                                                type="button"
                                                className="text-muted-foreground hover:text-foreground"
                                                onClick={() => onPointColorChange(label, '')}
                                                aria-label={`Limpar cor de ${label}`}
                                            >
                                                <Cross className="size-3.5" />
                                            </button>
                                        )}
                                        <ColorSwatchPicker
                                            value={pointColors[label] ?? ''}
                                            defaultColor={seriesBaseColor}
                                            brandColors={brandColors}
                                            onChange={(color) => onPointColorChange(label, color)}
                                            disabled={!hasTable}
                                            ariaLabel={`Cor da categoria ${label}`}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </CollapsibleSection>

            {/* RÓTULOS & DESTAQUES — value labels, per-group coloring, highlight. */}
            {hasLabelsGroup && (
                <CollapsibleSection
                    title="Rótulos & destaques"
                    icon={Tag}
                    open={activeSection === 'labels'}
                    onToggle={() => toggleSection('labels')}
                >
                    <>
                        {pointLabelsSupported && (
                            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
                                <Checkbox
                                    checked={pointLabels}
                                    disabled={!hasTable}
                                    onCheckedChange={(value) => onPointLabelsChange(value === true)}
                                />
                                Mostrar valores nos pontos
                            </label>
                        )}

                        {colorByGroupSupported && (
                            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-foreground">
                                <Checkbox
                                    className="mt-0.5"
                                    checked={colorByGroup}
                                    disabled={!hasTable}
                                    onCheckedChange={(value) =>
                                        onColorByGroupChange(value === true)
                                    }
                                />
                                <span>
                                    Colorir barras por grupo
                                    <span className="mt-0.5 block text-xs text-muted-foreground">
                                        Cada domínio recebe uma família de cor própria.
                                    </span>
                                </span>
                            </label>
                        )}

                        {deltasSupported && (
                            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-foreground">
                                <Checkbox
                                    className="mt-0.5"
                                    checked={showDeltas}
                                    disabled={!hasTable}
                                    onCheckedChange={(value) => onShowDeltasChange(value === true)}
                                />
                                <span>
                                    Mostrar evolução entre séries
                                    <span className="mt-0.5 block text-xs text-muted-foreground">
                                        Exibe ▲/▼ com a diferença entre a 1ª e a 2ª série em cada
                                        categoria.
                                    </span>
                                </span>
                            </label>
                        )}

                        {pointEmphasisSupported && (
                            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-foreground">
                                <Checkbox
                                    className="mt-0.5"
                                    checked={colorByClassification && classificationAvailable}
                                    disabled={!hasTable || !classificationAvailable}
                                    onCheckedChange={(value) =>
                                        onColorByClassificationChange(value === true)
                                    }
                                />
                                <span>
                                    Colorir pela classificação
                                    <span className="mt-0.5 block text-xs text-muted-foreground">
                                        {classificationAvailable
                                            ? 'Cada valor recebe a cor da faixa de referência em que cai.'
                                            : 'Adicione faixas de referência para habilitar.'}
                                    </span>
                                </span>
                            </label>
                        )}

                        {pointEmphasisSupported && xLabels.length > 0 && (
                            <div>
                                <FieldLabel hint="A categoria destacada ganha contorno escuro e valor em negrito.">
                                    Destacar categoria
                                </FieldLabel>
                                <Select
                                    value={highlightLabel || NO_HIGHLIGHT}
                                    onValueChange={(value: string) =>
                                        onHighlightLabelChange(value === NO_HIGHLIGHT ? '' : value)
                                    }
                                    disabled={!hasTable}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Nenhuma" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NO_HIGHLIGHT}>Nenhuma</SelectItem>
                                        {xLabels.map((label) => (
                                            <SelectItem key={`hl-${label}`} value={label}>
                                                {label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {highlightLabel && (
                                    <div className="mt-2 flex items-center gap-2">
                                        <span className="text-sm text-foreground">
                                            Cor do destaque
                                        </span>
                                        <ColorSwatchPicker
                                            value={highlightColor}
                                            defaultColor={seriesBaseColor}
                                            brandColors={brandColors}
                                            onChange={onHighlightColorChange}
                                            disabled={!hasTable}
                                            ariaLabel="Cor do destaque"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                </CollapsibleSection>
            )}

            {/* AJUSTES AVANÇADOS — reference bands/cutoffs/scale + per-type config panel. */}
            {hasAdvanced && (
                <CollapsibleSection
                    title={advancedTitle}
                    icon={Setting2}
                    open={activeSection === 'advanced'}
                    onToggle={() => toggleSection('advanced')}
                >
                    <>
                        {showDecorations && (
                            <DecorationsEditor
                                copy={decorationsCopy}
                                bands={bands}
                                cutoffs={cutoffs}
                                yAxisMin={yAxisMin}
                                yAxisMax={yAxisMax}
                                disabled={!hasTable}
                                onAddBand={onAddBand}
                                onUpdateBand={onUpdateBand}
                                onRemoveBand={onRemoveBand}
                                onAddCutoff={onAddCutoff}
                                onUpdateCutoff={onUpdateCutoff}
                                onRemoveCutoff={onRemoveCutoff}
                                onYAxisMinChange={onYAxisMinChange}
                                onYAxisMaxChange={onYAxisMaxChange}
                            />
                        )}

                        {ConfigPanel && (
                            <ConfigPanel
                                chartType={typeDef.id}
                                disabled={!hasTable}
                                quadrant={quadrant}
                                onQuadrantChange={onQuadrantChange}
                                axisEndLabels={axisEndLabels}
                                onAxisEndLabelsChange={onAxisEndLabelsChange}
                            />
                        )}
                    </>
                </CollapsibleSection>
            )}
        </div>
    );
}
